import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Trash2,
  Save,
  RotateCcw,
  GripVertical,
  Sparkles,
  X,
  AlertTriangle,
  ShieldCheck,
  Lightbulb,
  Check,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const MODULE_OPTIONS = [
  { value: "bang_ke_tien_mat", label: "Bảng kê tiền mặt" },
  { value: "bang_ke_doanh_thu", label: "Doanh thu Pippy" },
  { value: "doanh_thu_ngay", label: "Doanh thu ngày" },
  { value: "bao_cao_kd", label: "Báo cáo kinh doanh" },
  { value: "so_thu_chi", label: "Sổ thu chi" },
];

const SHEET_TYPE_OPTIONS = [
  { value: "__none__", label: "(Không)" },
  { value: "so_quy", label: "Sổ quỹ" },
  { value: "chung_tu", label: "Chứng từ đối chiếu" },
];

const MODULE_LABEL = {
  bang_ke_tien_mat: "Bảng kê tiền mặt",
  bang_ke_doanh_thu: "Doanh thu Pippy",
  doanh_thu_ngay: "Doanh thu ngày",
  bao_cao_kd: "Báo cáo kinh doanh",
  so_thu_chi: "Sổ thu chi",
};

const SHEET_LABEL = {
  so_quy: "Sổ quỹ",
  chung_tu: "Chứng từ đối chiếu",
};

function SortableRuleRow({ rule, onUpdate, onDelete, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const [edit, setEdit] = useState({ ...rule });
  const [busy, setBusy] = useState(false);
  const isDirty =
    edit.name !== rule.name ||
    edit.pattern !== rule.pattern ||
    edit.target_module !== rule.target_module ||
    (edit.sheet_type || null) !== (rule.sheet_type || null) ||
    edit.description !== rule.description;

  const handleSave = async () => {
    setBusy(true);
    try {
      await onUpdate(rule.id, {
        name: edit.name,
        pattern: edit.pattern,
        target_module: edit.target_module,
        sheet_type: edit.sheet_type || null,
        description: edit.description || "",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-[#0f172a] border ${
        rule.is_active ? "border-[#334155]" : "border-[#334155]/50 opacity-60"
      } rounded-lg p-3 grid grid-cols-12 gap-2 items-start`}
      data-testid={`classify-rule-row-${rule.id}`}
    >
      {/* Drag handle + priority */}
      <div className="col-span-1 flex items-center gap-1 pt-2">
        <button
          {...attributes}
          {...listeners}
          className="text-slate-600 hover:text-slate-300 cursor-grab active:cursor-grabbing"
          title="Kéo để đổi thứ tự"
          data-testid={`classify-rule-drag-${rule.id}`}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <Badge className="bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 text-[10px] font-mono">
          {rule.priority}
        </Badge>
      </div>

      {/* Name + description */}
      <div className="col-span-3 space-y-1">
        <Input
          value={edit.name}
          onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))}
          className="h-8 bg-[#1e293b] border-[#334155] text-slate-200 text-xs"
          placeholder="Tên rule"
          data-testid={`classify-rule-name-${rule.id}`}
        />
        <Input
          value={edit.description || ""}
          onChange={(e) => setEdit((p) => ({ ...p, description: e.target.value }))}
          className="h-7 bg-[#1e293b] border-[#334155] text-slate-500 text-[10px]"
          placeholder="Mô tả ngắn (tuỳ chọn)"
        />
        {rule.is_builtin && (
          <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[9px]">
            <ShieldCheck className="w-2.5 h-2.5 mr-0.5 inline" /> Mặc định
          </Badge>
        )}
      </div>

      {/* Pattern */}
      <div className="col-span-3">
        <Input
          value={edit.pattern}
          onChange={(e) => setEdit((p) => ({ ...p, pattern: e.target.value }))}
          className="h-8 bg-[#1e293b] border-[#334155] text-emerald-400 text-xs font-mono"
          placeholder="^bk... (regex)"
          data-testid={`classify-rule-pattern-${rule.id}`}
        />
        <p className="text-[9px] text-slate-600 mt-1">Regex (case-insensitive)</p>
      </div>

      {/* Target module */}
      <div className="col-span-2">
        <Select
          value={edit.target_module}
          onValueChange={(v) => setEdit((p) => ({ ...p, target_module: v }))}
        >
          <SelectTrigger
            className="h-8 bg-[#1e293b] border-[#334155] text-slate-200 text-xs"
            data-testid={`classify-rule-module-${rule.id}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1e293b] border-[#334155]">
            {MODULE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-slate-300 text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Sheet type */}
      <div className="col-span-1">
        <Select
          value={edit.sheet_type || "__none__"}
          onValueChange={(v) =>
            setEdit((p) => ({ ...p, sheet_type: v === "__none__" ? null : v }))
          }
        >
          <SelectTrigger
            className="h-8 bg-[#1e293b] border-[#334155] text-slate-200 text-xs"
            data-testid={`classify-rule-sheet-${rule.id}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1e293b] border-[#334155]">
            {SHEET_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-slate-300 text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Active toggle */}
      <div className="col-span-1 flex items-center justify-center pt-1.5">
        <Switch
          checked={!!rule.is_active}
          onCheckedChange={() => onToggle(rule.id, !rule.is_active)}
          className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-700"
          data-testid={`classify-rule-active-${rule.id}`}
        />
      </div>

      {/* Actions */}
      <div className="col-span-1 flex items-center gap-1 justify-end">
        {isDirty && (
          <Button
            onClick={handleSave}
            disabled={busy}
            size="sm"
            className="h-7 px-2 bg-indigo-500 hover:bg-indigo-400 text-white text-[10px]"
            data-testid={`classify-rule-save-${rule.id}`}
          >
            <Save className="w-3 h-3 mr-0.5" /> Lưu
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(rule.id, rule.name, rule.is_builtin)}
          className="h-7 w-7 p-0 text-slate-500 hover:text-red-400 hover:bg-red-500/10"
          data-testid={`classify-rule-delete-${rule.id}`}
          title={rule.is_builtin ? "Xoá rule mặc định" : "Xoá rule"}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function ClassifyRulesTab() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testFileName, setTestFileName] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [hints, setHints] = useState([]);
  const [hintBusy, setHintBusy] = useState({});  // {key: bool}
  const [newRule, setNewRule] = useState({
    name: "",
    pattern: "",
    target_module: "bang_ke_tien_mat",
    sheet_type: "__none__",
    description: "",
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/discord/classify-rules");
      setRules(data.rules || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi tải rules");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHints = useCallback(async () => {
    try {
      const { data } = await api.get("/discord/classify-rules/hints");
      setHints(data.hints || []);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchRules();
    fetchHints();
  }, [fetchRules, fetchHints]);

  const handleAcceptHint = async (hint) => {
    const key = `${hint.prefix}-${hint.target_module}`;
    setHintBusy((p) => ({ ...p, [key]: true }));
    try {
      await api.post("/discord/classify-rules/hints/accept", {
        prefix: hint.prefix,
        target_module: hint.target_module,
        sheet_type: hint.sheet_type,
        name: hint.suggested_name,
      });
      toast.success(`Đã tạo rule cho '${hint.prefix}*'`);
      fetchRules();
      fetchHints();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi tạo rule");
    } finally {
      setHintBusy((p) => ({ ...p, [key]: false }));
    }
  };

  const handleDismissHint = async (hint) => {
    const key = `${hint.prefix}-${hint.target_module}`;
    setHintBusy((p) => ({ ...p, [key]: true }));
    try {
      await api.post("/discord/classify-rules/hints/dismiss", {
        prefix: hint.prefix,
        target_module: hint.target_module,
        sheet_type: hint.sheet_type,
      });
      toast.success("Đã bỏ qua gợi ý");
      fetchHints();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi bỏ qua");
    } finally {
      setHintBusy((p) => ({ ...p, [key]: false }));
    }
  };

  const handleUpdate = async (id, fields) => {
    try {
      await api.put(`/discord/classify-rules/${id}`, fields);
      toast.success("Đã cập nhật rule");
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi cập nhật");
    }
  };

  const handleToggle = async (id, newActive) => {
    try {
      await api.put(`/discord/classify-rules/${id}`, { is_active: newActive });
      toast.success(newActive ? "Đã bật rule" : "Đã tắt rule");
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi toggle");
    }
  };

  const handleDelete = async (id, name, isBuiltin) => {
    const msg = isBuiltin
      ? `Xoá rule mặc định "${name}"? Bạn có thể khôi phục bằng nút "Khôi phục mặc định" sau này.`
      : `Xoá rule "${name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/discord/classify-rules/${id}`);
      toast.success("Đã xoá rule");
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi xoá");
    }
  };

  const handleAdd = async () => {
    if (!newRule.name.trim() || !newRule.pattern.trim()) {
      toast.error("Vui lòng nhập tên và pattern");
      return;
    }
    try {
      const maxPriority = rules.reduce((max, r) => Math.max(max, r.priority || 0), 0);
      await api.post("/discord/classify-rules", {
        name: newRule.name.trim(),
        pattern: newRule.pattern.trim(),
        target_module: newRule.target_module,
        sheet_type: newRule.sheet_type === "__none__" ? null : newRule.sheet_type,
        priority: maxPriority + 10,
        is_active: true,
        description: newRule.description.trim(),
      });
      toast.success("Đã thêm rule");
      setShowAdd(false);
      setNewRule({
        name: "",
        pattern: "",
        target_module: "bang_ke_tien_mat",
        sheet_type: "__none__",
        description: "",
      });
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi thêm rule");
    }
  };

  const handleResetDefaults = async () => {
    if (
      !window.confirm(
        "Khôi phục về 5 rules mặc định? Hành động này sẽ XOÁ TẤT CẢ rules hiện có và tạo lại từ đầu."
      )
    )
      return;
    try {
      const { data } = await api.post("/discord/classify-rules/seed-defaults");
      toast.success(data.message);
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi khôi phục");
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = rules.findIndex((r) => r.id === active.id);
    const newIndex = rules.findIndex((r) => r.id === over.id);
    const newRules = arrayMove(rules, oldIndex, newIndex);
    setRules(newRules); // optimistic UI

    try {
      await api.post("/discord/classify-rules/reorder", {
        rule_ids: newRules.map((r) => r.id),
      });
      toast.success("Đã đổi thứ tự");
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi đổi thứ tự");
      fetchRules();
    }
  };

  const handleTestFilename = async () => {
    if (!testFileName.trim()) {
      toast.error("Nhập tên file để test");
      return;
    }
    try {
      const { data } = await api.post("/discord/classify-rules/test", {
        file_name: testFileName.trim(),
      });
      setTestResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi test");
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-500" data-testid="classify-rules-loading">Đang tải rules…</div>;
  }

  return (
    <div className="space-y-4" data-testid="classify-rules-tab">
      {/* AI Hints Banner */}
      {hints.length > 0 && (
        <div className="bg-gradient-to-r from-emerald-500/10 to-indigo-500/10 border border-emerald-500/30 rounded-xl p-4 space-y-3" data-testid="classify-rules-hints-banner">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-emerald-400" />
            <h4 className="text-sm font-bold text-emerald-300">
              AI gợi ý {hints.length} quy tắc mới
            </h4>
            <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px]">
              Phát hiện từ lịch sử CEO duyệt tay
            </Badge>
          </div>
          <p className="text-[11px] text-emerald-200/80">
            Hệ thống phát hiện một số nhóm file CEO đã duyệt tay nhiều lần với cùng module — tạo rule để tự động hoá lần sau:
          </p>
          <div className="space-y-2">
            {hints.map((hint) => {
              const key = `${hint.prefix}-${hint.target_module}`;
              const busy = !!hintBusy[key];
              return (
                <div
                  key={key}
                  className="bg-[#0f172a]/70 border border-emerald-500/20 rounded-lg p-3"
                  data-testid={`classify-rules-hint-${hint.prefix}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="bg-emerald-500/15 text-emerald-300 px-2 py-0.5 rounded text-xs font-mono">
                          {hint.prefix}*
                        </code>
                        <span className="text-slate-400 text-xs">→</span>
                        <Badge className="bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 text-[10px]">
                          {MODULE_LABEL[hint.target_module] || hint.target_module}
                          {hint.sheet_type ? ` · ${SHEET_LABEL[hint.sheet_type] || hint.sheet_type}` : ""}
                        </Badge>
                        <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[10px]">
                          {hint.count} file
                        </Badge>
                      </div>
                      {hint.examples && hint.examples.length > 0 && (
                        <p className="text-[10px] text-slate-500 mt-1 truncate">
                          Ví dụ: {hint.examples.slice(0, 3).join(", ")}
                        </p>
                      )}
                      <p className="text-[10px] text-slate-600 mt-0.5 font-mono">
                        Pattern đề xuất: <span className="text-emerald-400">{hint.suggested_pattern}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleAcceptHint(hint)}
                        disabled={busy}
                        size="sm"
                        className="h-8 bg-emerald-500 hover:bg-emerald-400 text-white text-xs"
                        data-testid={`classify-rules-hint-accept-${hint.prefix}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> Tạo rule
                      </Button>
                      <Button
                        onClick={() => handleDismissHint(hint)}
                        disabled={busy}
                        variant="ghost"
                        size="sm"
                        className="h-8 text-slate-500 hover:text-red-400 hover:bg-red-500/10 text-xs"
                        data-testid={`classify-rules-hint-dismiss-${hint.prefix}`}
                      >
                        <X className="w-3.5 h-3.5 mr-1" /> Bỏ qua
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-5 space-y-3">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-base font-bold text-[#F8FAFC] flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-400" />
              Cấu hình Rule phân loại file Discord
            </h3>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              Khi file Discord được sync, hệ thống quét tên file qua các rules dưới đây (theo thứ tự ưu tiên) để
              tự động đẩy vào đúng module Kế toán. File <strong className="text-emerald-400">scan…</strong> sẽ
              đi thẳng vào <strong>Chứng từ đối chiếu</strong> và AI tự match vào dòng giao dịch trong Sổ thu chi.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowAdd((v) => !v)}
              variant="outline"
              className="border-indigo-500/40 text-indigo-400 hover:bg-indigo-500/10 h-8 text-xs"
              data-testid="classify-rules-add-toggle"
            >
              <Plus className="w-3 h-3 mr-1" /> Thêm rule
            </Button>
            <Button
              onClick={handleResetDefaults}
              variant="outline"
              className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 h-8 text-xs"
              data-testid="classify-rules-reset-defaults"
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Khôi phục mặc định
            </Button>
          </div>
        </div>

        {/* Test panel */}
        <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              value={testFileName}
              onChange={(e) => setTestFileName(e.target.value)}
              placeholder="Nhập tên file thử (vd: bk050326.xlsx, scan_0305.jpg, IMG_001.png)"
              className="h-8 bg-[#1e293b] border-[#334155] text-slate-200 text-xs flex-1 min-w-[300px]"
              data-testid="classify-rules-test-input"
              onKeyDown={(e) => e.key === "Enter" && handleTestFilename()}
            />
            <Button
              onClick={handleTestFilename}
              className="h-8 bg-emerald-500 hover:bg-emerald-400 text-white text-xs"
              data-testid="classify-rules-test-btn"
            >
              Thử match
            </Button>
          </div>
          {testResult && (
            <div className="mt-2 text-xs">
              {testResult.matched ? (
                <div className="text-emerald-400 flex items-center gap-2 flex-wrap">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  <span>
                    File <code className="bg-[#1e293b] px-1 rounded">{testResult.file_name}</code> khớp rule:{" "}
                    <strong>{testResult.matched.name}</strong>
                  </span>
                  <Badge className="bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 text-[9px]">
                    {MODULE_LABEL[testResult.matched.target_module] || testResult.matched.target_module}
                    {testResult.matched.sheet_type ? ` · ${SHEET_LABEL[testResult.matched.sheet_type] || testResult.matched.sheet_type}` : ""}
                  </Badge>
                </div>
              ) : (
                <div className="text-amber-400 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  <span>
                    Không rule nào match. File này sẽ rơi vào AI Vision fallback hoặc pending_review.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add rule inline form */}
        {showAdd && (
          <div className="bg-[#0f172a] border border-indigo-500/30 rounded-lg p-3 space-y-2" data-testid="classify-rules-add-form">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-indigo-400">Rule mới</span>
              <button
                onClick={() => setShowAdd(false)}
                className="text-slate-500 hover:text-slate-300"
                data-testid="classify-rules-add-close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
              <Input
                value={newRule.name}
                onChange={(e) => setNewRule((p) => ({ ...p, name: e.target.value }))}
                placeholder="Tên rule (vd: Lương)"
                className="md:col-span-3 h-8 bg-[#1e293b] border-[#334155] text-slate-200 text-xs"
                data-testid="classify-rules-add-name"
              />
              <Input
                value={newRule.pattern}
                onChange={(e) => setNewRule((p) => ({ ...p, pattern: e.target.value }))}
                placeholder="^lg[_\\s.-] (regex)"
                className="md:col-span-3 h-8 bg-[#1e293b] border-[#334155] text-emerald-400 text-xs font-mono"
                data-testid="classify-rules-add-pattern"
              />
              <Select
                value={newRule.target_module}
                onValueChange={(v) => setNewRule((p) => ({ ...p, target_module: v }))}
              >
                <SelectTrigger
                  className="md:col-span-2 h-8 bg-[#1e293b] border-[#334155] text-slate-200 text-xs"
                  data-testid="classify-rules-add-module"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  {MODULE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-slate-300 text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={newRule.sheet_type}
                onValueChange={(v) => setNewRule((p) => ({ ...p, sheet_type: v }))}
              >
                <SelectTrigger
                  className="md:col-span-2 h-8 bg-[#1e293b] border-[#334155] text-slate-200 text-xs"
                  data-testid="classify-rules-add-sheet"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  {SHEET_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-slate-300 text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleAdd}
                className="md:col-span-2 h-8 bg-indigo-500 hover:bg-indigo-400 text-white text-xs"
                data-testid="classify-rules-add-submit"
              >
                <Plus className="w-3 h-3 mr-1" /> Thêm
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Rules list */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-5 space-y-2" data-testid="classify-rules-list">
        {/* Header row */}
        <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold text-slate-500 uppercase pb-2 border-b border-[#334155]">
          <div className="col-span-1">#</div>
          <div className="col-span-3">Tên rule</div>
          <div className="col-span-3">Pattern (regex)</div>
          <div className="col-span-2">Module</div>
          <div className="col-span-1">Sheet</div>
          <div className="col-span-1 text-center">Bật</div>
          <div className="col-span-1 text-right">Hành động</div>
        </div>

        {rules.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-8">
            Chưa có rule nào. Bấm "Khôi phục mặc định" để tạo 5 rules preset.
          </p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={rules.map((r) => r.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {rules.map((rule) => (
                  <SortableRuleRow
                    key={rule.id}
                    rule={rule}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                    onToggle={handleToggle}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
